"use client";

/**
 * IndexCoverageLive — live breadth-of-coverage figures for the index.
 *
 * Companion to SearchPerformanceLive (which covers speed). This one answers
 * "what is actually in there" using the same public endpoints the askfinz.ai
 * status page reads.
 *
 * Aggregate totals only — the same endpoints also return `recent` arrays of
 * individual indexed URLs, which must not be surfaced here: they would publish
 * a live feed of exactly what the fleet is reading.
 */

import { useEffect, useState } from "react";
import { BookOpen, FileText, Layers, Type } from "lucide-react";
import { StatTileGrid } from "@/components/marketing/stat-tile";

type Docs = { indexed?: number; chars?: number; by_ext?: Record<string, number> };

const DOCS_URL = "https://extension.askfinz.ai/api/v1/search/docs/stats";

/* `compact()` lived here to render running totals. Those are gone while the index is being built,
   and the helper went with them rather than sitting unused waiting to be reached for again. */

export function IndexCoverageLive({ initial }: { initial?: Docs | null }) {
  /* Seeded by the server (see live-stats.server.tsx) so the figure is in the HTML on first
     paint rather than replacing a dash after hydration. The client fetch below stays: the page
     is prerendered on a 5-minute timer, and this is what keeps a cached copy current. */
  const [docs, setDocs] = useState<Docs | null>(initial ?? null);
  const [live, setLive] = useState(false);

  // The academic-insights fetch went with the "Scholarly works" total it fed. Left in place it
  // would be a request every 30 seconds, from every visitor, for a number nothing renders.
  useEffect(() => {
    let on = true;
    const load = async () => {
      try {
        const dr = await fetch(DOCS_URL, { cache: "no-store" });
        if (dr.ok) {
          const j = (await dr.json()) as Docs;
          if (on) {
            setDocs(j);
            setLive(true);
          }
        }
      } catch {
        /* transient — keep the last values */
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => {
      on = false;
      clearInterval(t);
    };
  }, []);

  /* What counts as a document format. `by_ext` is keyed on a trailing URL fragment, not a file
     type, so most of its 476 keys are noise — `php/anal`, `31083/rc`, `001`, and an `other` bucket
     holding a third of everything. A closed allowlist is right for this: we know what the formats
     are, and an unrecognised key should be excluded rather than counted. */
  const FORMATS = [
    "pdf", "docx", "doc", "pptx", "ppt", "xlsx", "xls", "csv", "tsv",
    "epub", "mobi", "rtf", "odt", "ods", "odp", "xml", "json", "bib", "ris",
  ];

  const byExt = docs?.by_ext ?? null;
  const formatCount = byExt
    ? FORMATS.filter((f) => (byExt[f] ?? 0) > 0).length
    : null;

  const indexed = typeof docs?.indexed === "number" && docs.indexed > 0 ? docs.indexed : null;
  const pdfShare =
    byExt && indexed ? Math.round(((byExt.pdf ?? 0) / indexed) * 100) : null;
  /* Characters in a typical document — a per-document average, not the total behind it. */
  const charsEach =
    typeof docs?.chars === "number" && indexed ? Math.round(docs.chars / indexed) : null;
  const fmtK = (n: number) => (n >= 1000 ? `${Math.round(n / 1000)}k` : String(n));

  const stats = [
    {
      icon: Layers,
      value: formatCount != null ? String(formatCount) : "—",
      label: "Document formats read",
      sub: "PDF, Office, ebooks and data files, converted to readable text",
    },
    {
      icon: FileText,
      value: pdfShare != null ? `${pdfShare}%` : "—",
      label: "Of documents are PDFs",
      sub: "read page by page, not linked to",
    },
    {
      icon: BookOpen,
      value: charsEach != null ? fmtK(charsEach) : "—",
      label: "Characters in a typical one",
      sub: "the whole document, not an abstract or a summary",
    },
    {
      icon: Type,
      value: "Continuous",
      label: "Refresh",
      sub: "read as it changes, not rebuilt on a schedule",
    },
  ];

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <span
          className={`inline-block h-2 w-2 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-neutral-400"}`}
          aria-hidden
        />
        <span className="kicker">{live ? "Live · coverage right now" : "Connecting…"}</span>
      </div>
      <StatTileGrid items={stats} />
    </div>
  );
}
